import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { useTreatmentPlan } from '@/hooks/useTreatmentPlan';
import { IconButton } from '@/components/ui/button';
import type { ToothCorrectionResponse } from '@/schema/toothCorrection';
import type { TreatmentPlanWeek } from '@/schema/treatmentPlan';

const fmt = (value: number) => value.toFixed(2);

function ToothTable({ title, teeth }: Readonly<{ title: string; teeth: ToothCorrectionResponse[] }>) {
  return (
    <div className="min-w-0 flex-1">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/55">
        {title} · {teeth.length}
      </h4>
      {teeth.length === 0 ? (
        <p className="text-xs text-white/40">No tooth movements.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-white/5 text-white/55">
                <th className="px-3 py-2 text-left font-medium">FDI</th>
                <th className="px-3 py-2 text-left font-medium">Movement (x, y, z) mm</th>
                <th className="px-3 py-2 text-left font-medium">Rotation (x, y, z, w)</th>
              </tr>
            </thead>
            <tbody>
              {[...teeth]
                .sort((a, b) => a.fdi - b.fdi)
                .map((tooth) => (
                  <tr key={tooth.id} className="border-t border-white/5 text-white/80">
                    <td className="px-3 py-1.5 font-medium text-white">{tooth.fdi}</td>
                    <td className="px-3 py-1.5 tabular-nums">{tooth.movement.map(fmt).join(', ')}</td>
                    <td className="px-3 py-1.5 tabular-nums">{tooth.rotation.map((r) => r.toFixed(3)).join(', ')}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WeekCard({ week, defaultOpen }: Readonly<{ week: TreatmentPlanWeek; defaultOpen: boolean }>) {
  const [open, setOpen] = useState(defaultOpen);
  const toothCount = week.upperJaw.length + week.lowerJaw.length;

  return (
    <section className="rounded-lg border border-white/10 bg-zinc-950/70">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold text-white">Week {week.treatmentWeek}</span>
        <span className="flex items-center gap-2 text-xs text-white/50">
          {toothCount} teeth
          <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      <CollapseBody open={open}>
        <div className="flex flex-col gap-6 px-4 pb-4 lg:flex-row">
          <ToothTable title="Upper jaw" teeth={week.upperJaw} />
          <ToothTable title="Lower jaw" teeth={week.lowerJaw} />
        </div>
      </CollapseBody>
    </section>
  );
}

/**
 * Smoothly animates its content height between 0 and the measured height, expanding
 * strictly downward (the trigger above it never moves). Settles to `auto` while open
 * so it reflows naturally.
 */
function CollapseBody({ open, children }: Readonly<{ open: boolean; children: React.ReactNode }>) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>(open ? 'auto' : 0);
  const prevOpen = useRef(open);

  if (prevOpen.current !== open) {
    prevOpen.current = open;
    const el = ref.current;
    const measured = el?.scrollHeight ?? 0;
    if (open) {
      setHeight(measured);
    } else {
      setHeight(measured);
      requestAnimationFrame(() => requestAnimationFrame(() => setHeight(0)));
    }
  }

  return (
    <div
      ref={ref}
      onTransitionEnd={() => open && setHeight('auto')}
      style={{ height: height === 'auto' ? 'auto' : `${height}px` }}
      className="overflow-hidden transition-[height] duration-300 ease-out"
    >
      {children}
    </div>
  );
}

export default function TreatmentPlanPage() {
  const { scanId } = useParams<{ scanId: string }>();
  useDocumentTitle('Treatment plan');
  const navigate = useNavigate();

  const planQuery = useTreatmentPlan(scanId ?? '', { enabled: Boolean(scanId) });

  if (!scanId) {
    return <div className="p-6 text-white">Invalid scan reference.</div>;
  }

  if (planQuery.isLoading) {
    return <div className="flex h-full items-center justify-center text-white/70">Loading treatment plan…</div>;
  }

  if (planQuery.isError || !planQuery.data) {
    return <div className="p-6 text-white">No treatment plan available for this scan.</div>;
  }

  const plan = planQuery.data;
  const patientName = [plan.patient.firstName, plan.patient.lastName].filter(Boolean).join(' ');
  const dob = plan.patient.dateOfBirth ? format(new Date(plan.patient.dateOfBirth), 'MMM d, yyyy') : '—';

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 text-white">
      <div className="flex shrink-0 items-center gap-3">
        <IconButton variant="ghost-light" size="sm" icon={ArrowLeft} onClick={() => navigate(-1)} />
        <div className="space-y-1">
          <h2 className="text-xl font-semibold tracking-tight text-white">Treatment plan</h2>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55">
            <span>Patient: {patientName}</span>
            <span>Date of birth: {dob}</span>
            <span>Weeks: {plan.weeks.length}</span>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
        {plan.weeks.length === 0 ? (
          <p className="text-sm text-white/60">This scan has no correction steps yet.</p>
        ) : (
          plan.weeks.map((week, index) => (
            <WeekCard key={week.correctionId} week={week} defaultOpen={index === 0} />
          ))
        )}
      </div>
    </div>
  );
}
